import usePrivateAxios from '@/common/api/usePrivateAxios'
import { defaultTransformerResponse } from '@/common/constants/defaultTransformerResponse.constant'
import { Mapper } from '@/common/services/mapper'
import { MetaResponse } from '@/common/types/metaResponse'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

type UseMaintenanceWorkflowsProps<T> = {
  filters?: {
    search?: string;
    page?: number;
    companyId?: string;
    status?: string;
    equipmentId?: string;
    branchId?: string;
    currentForwardingStatus?: string;
    checkinEntryType?: string;
    establishmentChoiceEntryType?: string;
    financialReviewEntryType?: string;
    directiveEntryType?: string;
    fiscalAssessmentEntryType?: string;
    checkoutEntryType?: string;
    actualEntry?: { relation?: string; entry_klass?: string | null; status?: string | null } | null;
  };
  mapper?: (data: any) => T;
  enabled?: boolean;
}

export const useMaintenanceWorkflows = <T,>({
  filters: {
    search = '',
    page = 1,
    companyId,
    status,
    equipmentId,
    branchId,
    currentForwardingStatus,
    checkinEntryType,
    establishmentChoiceEntryType,
    financialReviewEntryType,
    directiveEntryType,
    fiscalAssessmentEntryType,
    checkoutEntryType,
    actualEntry,
  } = {},
  mapper = Mapper.default<T>(),
  enabled,
}: UseMaintenanceWorkflowsProps<T>) => {
  const privateAxios = usePrivateAxios()

  const { data: { data, meta } = defaultTransformerResponse<T>(), ...rest } = useQuery({
    queryKey: [
      'maintenanceWorkflows',
      companyId,
      search,
      page,
      status,
      equipmentId,
      branchId,
      currentForwardingStatus,
      checkinEntryType,
      establishmentChoiceEntryType,
      financialReviewEntryType,
      directiveEntryType,
      fiscalAssessmentEntryType,
      checkoutEntryType,
      actualEntry?.relation,
      actualEntry?.entry_klass,
      actualEntry?.status,
    ],
    queryFn: () => {
      return privateAxios.get(`/maintenance/workflows`, {
        params: {
          search,
          page,
          'q[company_id_eq]': companyId,
          'q[status_eq]': status,
          'q[equipment_id_eq]': equipmentId,
          'q[branch_id_eq]': branchId,
          'q[current_forwarding_status_eq]': currentForwardingStatus,
          'q[current_forwarding_checkin_entry_type_eq]': checkinEntryType,
          'q[current_forwarding_establishment_choice_entry_type_eq]': establishmentChoiceEntryType,
          'q[current_forwarding_financial_review_entry_type_eq]': financialReviewEntryType,
          'q[current_forwarding_directive_entry_type_eq]': directiveEntryType,
          'q[current_forwarding_fiscal_assessment_entry_type_eq]': fiscalAssessmentEntryType,
          'q[current_forwarding_checkout_entry_type_eq]': checkoutEntryType,
          'q[current_forwarding_actual_entry][relation]': actualEntry?.relation,
          'q[current_forwarding_actual_entry][entry_klass]': actualEntry?.entry_klass,
          'q[current_forwarding_actual_entry][status]': actualEntry?.status,
        },
      })
    },
    select: (data: { data: { data: any[]; meta: MetaResponse } }) => {
      return {
        data: mapper(data?.data?.data),
        meta: data?.data?.meta,
      }
    },
    placeholderData: keepPreviousData,
    enabled: typeof enabled === 'boolean' ? enabled : true,
  });

  return { data, meta, ...rest }
}
